import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { ServerAssistedResultMessage } from '@orborix/shared-types';
import type { ConnectionStatus, VoiceMessage } from '@orborix/voice-client';
import {
  discardAssistedPhrase,
  fetchReplySuggestions,
  SuggestError,
  type InspireSuggestion,
  type SuggestMessage,
} from '../../../lib/suggestApi.js';
import { ASSISTED_COPY } from './assistedCopy.js';
import { createSilenceEnder, TAP_SILENCE_MS } from '../../../lib/silenceEnder.js';
import {
  acceptedBubbleNote,
  createVoiceCounter,
  karaokeMarks,
  type AssistedResult,
  type KaraokeMark,
  type VoiceCounter,
} from './assistedModel.js';

/**
 * La vuelta asistida de un escenario (encargo 99, diseño cerrado de #468).
 *
 * Después de cada turno del coach, la app arma UNA respuesta modelo (la
 * «directa» de Inspirar, ya calibrada al nivel) y se la muestra al alumno, que
 * la DICE con el micrófono. El server la juzga (`AssistedTurnGate`, vara
 * tolerante + segunda opinión) y contesta `assisted.result`:
 *   - aceptada → celebración corta y la charla sigue sola, sin nada para leer;
 *   - no aceptada → la tarjeta dice qué oyó y ofrece «Decila una vez más» o «Seguir»
 *     (nunca es un examen); el turno no llegó al coach.
 * Lo dicho con ayuda no cuenta para la misión: el server emite sus objetivos
 * como PRACTICADOS, y cuando están todos se ofrece «¿Probás la escena solo?».
 * «Hacelo solo» está siempre: apaga la ayuda en esta charla y tapa lo que el
 * alumno dijo con ayuda hasta que termine.
 */
export type AssistedPhase =
  /** Sin ayuda: modo solo, o la charla no es asistida. */
  | 'off'
  /** Hoja de instrucciones, antes de la primera respuesta modelo. */
  | 'intro'
  /** Esperando el turno del coach. */
  | 'waiting'
  | 'loading'
  /** Respuesta modelo a la vista: esperando el micrófono. */
  | 'ready'
  | 'recording'
  /** Soltó: el server está juzgando. */
  | 'judging'
  /** Aceptada: la celebración corre sobre la tarjeta. */
  | 'accepted'
  | 'rejected'
  | 'silence'
  | 'error'
  /** Practicó todos los objetivos: la oferta de hacerlo solo. */
  | 'offer';

interface AssistedSession {
  sendSessionUpdate: (patch: Record<string, unknown>) => void;
  sendText: (text: string, opts?: { fromSuggestion?: boolean }) => void;
  turnEmptyCount: number;
  transcript: string;
  connectionStatus: ConnectionStatus;
}

export interface UseAssistedTurnArgs {
  /** Escenario curado con misión, empezado en «Asistida». Se lee al montar. */
  active: boolean;
  /** Título de la escena (hoja de instrucciones). */
  sceneTitle: string;
  /** El escenario curado (lo anota «Descartar esta frase»). */
  scenarioId?: string | null;
  /**
   * Los objetivos de la misión, en orden (los principales y el extra al final,
   * `assistedObjectives`): todos practicados → oferta; los que faltan van al
   * pedido de la respuesta modelo (Emmanuel, 14/10: antes no sabía de la misión
   * y apuntaba a los objetivos sólo de casualidad).
   */
  objectives: ReadonlyArray<{ id: string; label: string; bonus?: boolean }>;
  session: AssistedSession | null;
  sessionId: string | null;
  messages: ReadonlyArray<VoiceMessage>;
  assistantText: string;
  /**
   * `sending` = «enviando…» al soltar; descartar la toma no lo prende. `locked` =
   * la toma quedó abierta con un toque; `send` la manda.
   */
  recorder: {
    recording: boolean;
    sending: boolean;
    locked: boolean;
    getEnergy: () => number;
    send: () => void;
  };
  ended: boolean;
  /** «Probar solo»: la misma escena de nuevo, sin ayuda. */
  onRestartSolo: () => void;
}

export interface UseAssistedTurnReturn {
  phase: AssistedPhase;
  sceneTitle: string;
  /** La tarjeta de la respuesta modelo (o de la oferta) está a la vista. */
  cardVisible: boolean;
  /**
   * La hoja de la asistida va al final de la charla (en lugar del composer):
   * toda la escena con ayuda, también mientras habla el coach (apagada). Fuera
   * de ella —«Hacelo solo»—, la charla de siempre.
   */
  deckVisible: boolean;
  /** La oferta: `true` = practicó todos (también el extra); `false` = sólo los principales. */
  offerAllDone: boolean;
  /** Los objetivos de la misión, en orden (la oferta los muestra «con ayuda»). */
  objectives: ReadonlyArray<{ id: string; label: string; bonus?: boolean }>;
  model: InspireSuggestion | null;
  /** Número de respuesta modelo de esta charla (1, 2, …). */
  turnNo: number;
  /** Marca de karaoke por palabra visible de la frase modelo. */
  marks: KaraokeMark[];
  result: AssistedResult | null;
  error: string | null;
  /** El alumno pasó a «Hacelo solo» en esta charla (muestra la tarjeta «Ahora solo» hasta su primer turno). */
  soloSince: number | null;
  /**
   * Notita de una burbuja del alumno dicha con ayuda: `index` es su lugar en
   * `messages` (la misma frase dicha después, sola, no la lleva).
   */
  noteFor: (text: string, index: number) => string | null;
  /** Una burbuja dicha con ayuda queda tapada mientras hace la escena solo (hasta que termina). */
  isCovered: (text: string, index: number) => boolean;
  start: () => void;
  goSolo: () => void;
  /**
   * «Descartar esta frase» (Emmanuel, 19/10): desaparece como si nunca hubiera
   * llegado —no cuenta, no va al repaso— y llega otra, más simple. El server
   * la anota para revisar al escritor.
   */
  discard: () => void;
  /**
   * El alumno tocó la hoja de la frase y todavía no habló: le avisa al server que
   * está (está leyendo), así el «¿seguís ahí?» de los 60 s sin habla vuelve a
   * contar (encargo 113: le caía leyendo). Sólo antes del primer turno.
   */
  present: () => void;
  follow: () => void;
  repeat: () => void;
  retry: () => void;
  keepAssisted: () => void;
  restartSolo: () => void;
  celebrationDone: () => void;
}

const MAX_CONTEXT_TURNS = 12;
/** Aviso de presencia: a lo sumo uno cada tanto (cada toque no es un mensaje). */
const PRESENT_EVERY_MS = 10_000;
/** Juzgando sin veredicto (un turno que se perdió en el camino): vuelve a la frase. */
const JUDGING_WATCHDOG_MS = 15_000;
/** La frase modelo que no llega: error con «Reintentar» (y «Hacelo solo»). */
const MODEL_TIMEOUT_MS = 20_000;
/**
 * Fases en las que el próximo texto del coach es el que va a llevar frase: se
 * pide apenas termina de escribirlo. También con la hoja de instrucciones a la
 * vista (el saludo se escribe mientras la lee) y con la celebración de un
 * acierto (el coach ya está contestando): si no, se pedía recién cuando el coach
 * terminaba de HABLAR (Emmanuel, 16/10).
 */
const PREFETCH_PHASES: ReadonlySet<AssistedPhase> = new Set(['intro', 'waiting', 'accepted']);
/** Fases con la frase a la vista sin turno propio en vuelo. */
const IDLE_CARD_PHASES: ReadonlySet<AssistedPhase> = new Set([
  'loading',
  'ready',
  'rejected',
  'silence',
  'error',
]);
const copy = ASSISTED_COPY.es;

/**
 * Con los principales practicados, cuántas frases más se busca el objetivo
 * extra antes de ofrecer igual hacerla solo: a veces no entra (en soporte, un
 * coach que coopera no da lugar a «insistir») y la charla no terminaba nunca.
 */
const BONUS_TRIES = 3;

/** Frases seguidas sin objetivo después de las cuales la siguiente tiene que hacer uno. */
const PUSH_GOAL_AFTER = 1;

/** La respuesta modelo pedida y los objetivos pendientes que se mandaron (en ese orden). */
interface ModelRequest {
  suggestions: InspireSuggestion[];
  goalIds: string[];
}

export function useAssistedTurn(args: UseAssistedTurnArgs): UseAssistedTurnReturn {
  const { session, messages, assistantText, recorder, ended } = args;
  const [phase, setPhase] = useState<AssistedPhase>(() => (args.active ? 'intro' : 'off'));
  const [model, setModel] = useState<InspireSuggestion | null>(null);
  const [turnNo, setTurnNo] = useState(0);
  const [result, setResult] = useState<AssistedResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  // Cada notita es de la PRIMERA burbuja del alumno con ese texto que llegue
  // después de anotarla (`from` = largo de `messages` en ese momento).
  const [notes, setNotes] = useState<ReadonlyArray<{ text: string; note: string; from: number }>>(
    [],
  );
  const messagesLenRef = useRef(messages.length);
  messagesLenRef.current = messages.length;
  const [covered, setCovered] = useState(false);
  const [soloSince, setSoloSince] = useState<number | null>(null);
  const [saidSyllables, setSaidSyllables] = useState(0);
  const [offerPending, setOfferPending] = useState(false);
  const offerPendingRef = useRef(offerPending);
  offerPendingRef.current = offerPending;
  const practicedRef = useRef<Set<string>>(new Set());
  /** Frases modelo seguidas que no hicieron ningún objetivo pendiente. */
  const dryRef = useRef(0);
  /** Frases modelo con los principales ya practicados y el extra todavía no. */
  const bonusTriesRef = useRef(0);
  /** ¿Están todos los objetivos practicados, también el extra? (la oferta lo dice) */
  const [offerAllDone, setOfferAllDone] = useState(true);
  const objectivesRef = useRef(args.objectives);
  objectivesRef.current = args.objectives;
  const handledCoachRef = useRef<string | null>(null);
  const expectedRef = useRef<string | null>(null);
  // Lo mandado con la frase (objetivo y traducción): se re-manda ENTERO tras una
  // reconexión; sin el objetivo, el server lo perdía.
  const expectedPatchRef = useRef<Record<string, unknown> | null>(null);
  /** Las frases descartadas para el turno del coach que se está contestando. */
  const discardedRef = useRef<string[]>([]);
  const reqRef = useRef(0);
  const counterRef = useRef<VoiceCounter | null>(null);
  const emptyCountRef = useRef(session?.turnEmptyCount ?? 0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const sessionRef = useRef(session);
  sessionRef.current = session;
  const endedRef = useRef(ended);
  endedRef.current = ended;

  const userTurns = messages.filter((m) => m.who === 'user').length;
  const userTurnsRef = useRef(userTurns);
  userTurnsRef.current = userTurns;
  const lastCoachText = useMemo(
    () => [...messages].reverse().find((m) => m.who === 'bot')?.text ?? '',
    [messages],
  );

  // `goal`: el objetivo de la escena que hace la frase. Lo practicado con ayuda
  // es sólo ése (Emmanuel, 14/10: aceptar lo que ofreció el coach contaba).
  // `es`: su traducción, la pista del repaso si la sigue sin intentarla (19/10).
  const setExpected = useCallback(
    (text: string | null, goal?: string, es?: string): void => {
      expectedRef.current = text;
      const patch = {
        assistedExpect: text,
        ...(goal !== undefined ? { assistedGoal: goal } : {}),
        ...(text !== null && es?.trim() ? { assistedEs: es.trim() } : {}),
      };
      expectedPatchRef.current = text === null ? null : patch;
      session?.sendSessionUpdate(patch);
    },
    [session],
  );

  const addNote = useCallback((text: string, note: string): void => {
    const from = messagesLenRef.current;
    setNotes((prev) => [...prev, { text: text.trim(), note, from }]);
  }, []);

  // El pedido de la respuesta modelo: la charla (hasta `coachText` si el coach
  // todavía no cerró su burbuja) y los objetivos que faltan practicar.
  const requestModel = useCallback(
    (coachText: string | null, avoid: ReadonlyArray<string> = []): Promise<ModelRequest> => {
      const ctx: SuggestMessage[] = [];
      for (const m of messages) {
        if (m.who === 'user') ctx.push({ role: 'user', content: m.text });
        else if (m.who === 'bot') ctx.push({ role: 'assistant', content: m.text });
      }
      if (coachText !== null) ctx.push({ role: 'assistant', content: coachText });
      const context = ctx.filter((m) => m.content.trim().length > 0).slice(-MAX_CONTEXT_TURNS);
      const pending = objectivesRef.current.filter((o) => !practicedRef.current.has(o.id));
      // Tras una frase sin objetivo, ésta TIENE que hacer uno (era tras dos hasta
      // el 20/10: Emmanuel, «tardamos demasiado en alcanzar los objetivos»): si no,
      // con un coach que sólo pregunta la escena no terminaba (simulación «En el
      // médico», 14/10: ocho frases que sólo contestaban).
      const push = pending.length > 0 && dryRef.current >= PUSH_GOAL_AFTER;
      return fetchReplySuggestions(
        context,
        args.sessionId ?? undefined,
        pending.map((o) => o.label),
        {
          assisted: true,
          ...(push ? { pushGoal: true } : {}),
          ...(avoid.length > 0 ? { avoid } : {}),
        },
      ).then((suggestions) => ({ suggestions, goalIds: pending.map((o) => o.id) }));
    },
    [messages, args.sessionId],
  );

  const requestModelRef = useRef(requestModel);
  requestModelRef.current = requestModel;

  // Pedida por adelantado (apenas el coach terminó de ESCRIBIR, mientras su voz
  // sigue sonando — Emmanuel, 14/10): se usa si la burbuja que se cierra es esa.
  // `pending` = los objetivos que faltaban al pedirla.
  const prefetchRef = useRef<{
    text: string;
    request: Promise<ModelRequest>;
    pending: ReadonlyArray<string>;
  } | null>(null);
  const pendingIds = (): string[] =>
    objectivesRef.current.filter((o) => !practicedRef.current.has(o.id)).map((o) => o.id);

  const fetchModel = useCallback(
    async (prefetched?: Promise<ModelRequest>, avoid?: ReadonlyArray<string>): Promise<void> => {
      const reqId = ++reqRef.current;
      setPhase('loading');
      // También en el ref, ya: una frase pedida por adelantado puede estar resuelta
      // y seguir en el mismo tick, antes del render — con el ref viejo («waiting»)
      // se descartaba como vieja y la tarjeta quedaba «armando» para siempre.
      phaseRef.current = 'loading';
      setError(null);
      setResult(null);
      try {
        const pick = async (
          request: Promise<ModelRequest>,
        ): Promise<{ direct: InspireSuggestion; goal: string | undefined; goalIds: string[] }> => {
          let timer: ReturnType<typeof setTimeout> | undefined;
          const { suggestions, goalIds } = await Promise.race([
            request,
            new Promise<never>((_, reject) => {
              timer = setTimeout(
                () => reject(new Error('suggest-replies: sin respuesta')),
                MODEL_TIMEOUT_MS,
              );
            }),
          ]).finally(() => clearTimeout(timer));
          // La «directa» de Inspirar: el prompt arma siempre una de cada ángulo.
          const direct = suggestions.find((s) => s.angle === 'direct');
          if (!direct) throw new Error('suggest-replies: no vino la sugerencia «direct»');
          const goal = direct.goal === undefined ? undefined : goalIds[direct.goal];
          if (direct.goal !== undefined && goal === undefined) {
            throw new Error(`suggest-replies: objetivo ${direct.goal} fuera de los pedidos`);
          }
          return { direct, goal, goalIds };
        };
        let picked = await pick(prefetched ?? requestModel(null, avoid));
        if (reqId !== reqRef.current || phaseRef.current !== 'loading') return;
        // Escrita para un objetivo que se practicó mientras se pedía (el juez del
        // turno llega después de que el coach terminó de escribir): se pide otra,
        // ya sin él en la lista.
        if (picked.goal !== undefined && practicedRef.current.has(picked.goal)) {
          picked = await pick(requestModel(null, avoid));
          if (reqId !== reqRef.current || phaseRef.current !== 'loading') return;
        }
        const { direct, goal, goalIds } = picked;
        dryRef.current = goal === undefined && goalIds.length > 0 ? dryRef.current + 1 : 0;
        const objs = objectivesRef.current;
        const mainDone = objs.every((o) => o.bonus === true || practicedRef.current.has(o.id));
        if (mainDone && objs.some((o) => !practicedRef.current.has(o.id))) {
          bonusTriesRef.current += 1;
        }
        setModel(direct);
        setTurnNo((n) => n + 1);
        setExpected(direct.en, goal, direct.es);
        setPhase('ready');
      } catch (err) {
        if (reqId !== reqRef.current) return;
        console.warn('[assisted] model answer failed', err);
        setError(err instanceof SuggestError && err.message ? err.message : copy.card.error);
        setPhase('error');
      }
    },
    [requestModel, setExpected],
  );

  // El coach terminó de escribir (su voz puede seguir sonando): se pide ya.
  useEffect(() => {
    if (!args.active) return;
    const onTextComplete = (ev: Event): void => {
      const text = (ev as CustomEvent<{ text?: unknown }>).detail?.text;
      if (typeof text !== 'string' || !text.trim()) return;
      if (!PREFETCH_PHASES.has(phaseRef.current) || endedRef.current || offerPendingRef.current) {
        return;
      }
      if (prefetchRef.current?.text === text.trim()) return;
      const request = requestModelRef.current(text);
      request.catch(() => {}); // si no se usa, que no quede un rechazo suelto
      prefetchRef.current = { text: text.trim(), request, pending: pendingIds() };
    };
    window.addEventListener('orborix:coach.text-complete', onTextComplete);
    return () => window.removeEventListener('orborix:coach.text-complete', onTextComplete);
  }, [args.active]);

  // Turno nuevo del coach (terminado de escribir) → la respuesta modelo, o la
  // oferta de hacerlo solo si ya practicó todos los objetivos. También con la
  // tarjeta a la vista: si el alumno escribió (o dijo) otra cosa, el coach ya
  // contestó y la frase vieja no responde a nada — se arma la nueva.
  useEffect(() => {
    if (ended) return;
    if (phase !== 'waiting' && !IDLE_CARD_PHASES.has(phase)) return;
    if (!lastCoachText.trim() || assistantText.trim() !== '') return;
    if (handledCoachRef.current === lastCoachText) return;
    handledCoachRef.current = lastCoachText;
    if (phase !== 'waiting' && expectedRef.current) setExpected(null);
    // Un turno nuevo del coach: lo descartado era para el anterior.
    discardedRef.current = [];
    const prefetched = prefetchRef.current;
    prefetchRef.current = null;
    // El extra no entró en BONUS_TRIES frases: la oferta sale igual.
    if (offerPending || bonusTriesRef.current >= BONUS_TRIES) {
      if (!offerPending) {
        setOfferAllDone(false);
        setOfferPending(true);
      }
      setPhase('offer');
      return;
    }
    void fetchModel(prefetched?.text === lastCoachText.trim() ? prefetched.request : undefined);
  }, [phase, lastCoachText, assistantText, ended, offerPending, fetchModel, setExpected]);

  // El micrófono: apretó → grabando (cursor de voz); soltó → el server juzga.
  // Soltar descartando (tacho, deslizar, turno cortado) no manda nada: nadie
  // va a juzgar, vuelve a la frase.
  useEffect(() => {
    if (recorder.recording && (phase === 'ready' || phase === 'rejected' || phase === 'silence')) {
      counterRef.current = createVoiceCounter();
      setSaidSyllables(0);
      setResult(null);
      setPhase('recording');
    } else if (!recorder.recording && phase === 'recording') {
      setPhase(recorder.sending ? 'judging' : 'ready');
    }
  }, [recorder.recording, recorder.sending, phase]);

  // Red de seguridad: un veredicto que no llega (el turno se perdió entre el
  // cliente y el portero) no puede dejar la tarjeta juzgando para siempre.
  useEffect(() => {
    if (phase !== 'judging') return;
    const id = setTimeout(() => {
      if (phaseRef.current !== 'judging') return;
      console.warn('[assisted] no verdict from the server — back to the model answer');
      if (expectedPatchRef.current) {
        sessionRef.current?.sendSessionUpdate(expectedPatchRef.current);
      }
      setResult(null);
      setPhase('ready');
    }, JUDGING_WATCHDOG_MS);
    return () => clearTimeout(id);
  }, [phase]);

  // El cursor de voz: una ventana de nivel cada ~50 ms, con el tiempo REAL entre
  // ventanas (el render puede atrasar el timer). El recorder cambia de identidad
  // en cada render: se lee por ref para no rearmar el intervalo.
  const recorderRef = useRef(recorder);
  recorderRef.current = recorder;
  // Con un toque (la toma quedó abierta), se manda sola cuando el alumno se
  // calla, como en el repaso (Emmanuel, 20/10: mantener, tocar y tocar, o tocar
  // y que se mande solo). Mismo detector y mismo margen que el repaso.
  useEffect(() => {
    if (phase !== 'recording') return;
    let last = performance.now();
    const ender = createSilenceEnder();
    let armed = false;
    let sent = false;
    const id = setInterval(() => {
      const c = counterRef.current;
      const now = performance.now();
      const dt = Math.min(200, now - last);
      last = now;
      const rec = recorderRef.current;
      const energy = rec.getEnergy();
      if (c) {
        c.window(energy, dt);
        setSaidSyllables(c.syllables());
      }
      if (!armed && rec.locked) {
        ender.arm(TAP_SILENCE_MS);
        armed = true;
      }
      if (ender.window(energy, dt) && armed && !sent && rec.recording && rec.locked) {
        sent = true;
        rec.send();
      }
    }, 50);
    return () => clearInterval(id);
  }, [phase]);

  // Soltó sin que el STT oyera nada: «No te escuché».
  useEffect(() => {
    const count = session?.turnEmptyCount ?? 0;
    if (count <= emptyCountRef.current) return;
    emptyCountRef.current = count;
    if (phaseRef.current === 'judging' || phaseRef.current === 'recording') {
      setResult({
        accepted: false,
        state: 'silence',
        heard: '',
        marks: [],
        missing: [],
        extra: [],
        secondOpinion: false,
      });
      setPhase('silence');
    }
  }, [session?.turnEmptyCount]);

  // El veredicto del server.
  useEffect(() => {
    if (!args.active) return;
    const onResult = (ev: Event): void => {
      const msg = (ev as CustomEvent<ServerAssistedResultMessage>).detail;
      const p = phaseRef.current;
      // `rejected`/`silence` también: en manos libres el reintento llega sin
      // apretar el mic, así que la fase no pasa por «grabando».
      if (
        p !== 'judging' &&
        p !== 'recording' &&
        p !== 'ready' &&
        p !== 'rejected' &&
        p !== 'silence'
      ) {
        return;
      }
      const { type: _type, ...r } = msg;
      setResult(r);
      if (r.accepted) {
        expectedRef.current = null;
        expectedPatchRef.current = null;
        addNote(r.heard, acceptedBubbleNote(r, copy));
        setPhase('accepted');
      } else {
        setPhase(r.state === 'silence' ? 'silence' : 'rejected');
      }
    };
    window.addEventListener('orborix:assisted.result', onResult);
    return () => window.removeEventListener('orborix:assisted.result', onResult);
  }, [args.active, addNote]);

  // Objetivos PRACTICADOS con ayuda → con todos, la oferta de hacerlo solo.
  const objectivesKey = args.objectives.map((o) => o.id).join('|');
  useEffect(() => {
    if (!args.active) return;
    const all = objectivesKey ? objectivesKey.split('|') : [];
    const onProgress = (ev: Event): void => {
      const d = (ev as CustomEvent).detail as
        | { objectiveId?: unknown; assisted?: unknown }
        | undefined;
      if (!d || d.assisted !== true || typeof d.objectiveId !== 'string') return;
      practicedRef.current.add(d.objectiveId);
      // La frase pedida por adelantado contaba con ese objetivo pendiente: se pide
      // de nuevo, sin él, mientras el coach sigue hablando (20/10: «En el médico»,
      // la segunda frase salió para los síntomas que la primera ya había hecho).
      const pf = prefetchRef.current;
      if (pf?.pending.includes(d.objectiveId)) {
        const request = requestModelRef.current(pf.text);
        request.catch(() => {});
        prefetchRef.current = { text: pf.text, request, pending: pendingIds() };
      }
      if (all.length > 0 && all.every((id) => practicedRef.current.has(id))) {
        setOfferAllDone(true);
        setOfferPending(true);
      }
    };
    window.addEventListener('orborix:objective.progress', onProgress);
    return () => window.removeEventListener('orborix:objective.progress', onProgress);
  }, [args.active, objectivesKey]);

  // Una reconexión arma otro pipeline en el server: la frase esperada se
  // re-manda cuando el server avisa que está listo (`session.started`; lo que
  // se manda al abrir el socket se pierde: su transport todavía no escucha).
  // Sin eso, lo que el alumno dijera pasaba al coach sin juzgar. Un turno que
  // estaba en vuelo durante el corte se perdió: vuelve a la frase. (El objeto
  // de la sesión cambia de identidad en cada render: se lee por ref.)
  useEffect(() => {
    if (!args.active) return;
    const onStarted = (): void => {
      if (expectedPatchRef.current) {
        sessionRef.current?.sendSessionUpdate(expectedPatchRef.current);
      }
    };
    window.addEventListener('orborix:session.started', onStarted);
    return () => window.removeEventListener('orborix:session.started', onStarted);
  }, [args.active]);
  const connectionStatus = session?.connectionStatus;
  const prevStatusRef = useRef(connectionStatus);
  useEffect(() => {
    const prev = prevStatusRef.current;
    prevStatusRef.current = connectionStatus;
    if (connectionStatus !== 'open' || prev === 'open' || prev === undefined) return;
    if (phaseRef.current === 'recording' || phaseRef.current === 'judging') {
      setResult(null);
      setPhase('ready');
    }
  }, [connectionStatus]);

  // La tarjeta «Ahora solo» se va con el primer turno propio.
  useEffect(() => {
    if (soloSince !== null && userTurns > soloSince) setSoloSince(null);
  }, [soloSince, userTurns]);

  useEffect(() => {
    if (ended && phase !== 'off') setPhase('off');
  }, [ended, phase]);

  // Después del primer turno el server ya no cuenta silencios: no hace falta.
  const presentAtRef = useRef<number | null>(null);
  const present = useCallback((): void => {
    if (userTurnsRef.current > 0) return;
    const now = Date.now();
    if (presentAtRef.current !== null && now - presentAtRef.current < PRESENT_EVERY_MS) return;
    presentAtRef.current = now;
    sessionRef.current?.sendSessionUpdate({ assistedPresent: true });
  }, []);
  // Cerrar la hoja de instrucciones no avisa: la sesión se abre recién ahí (el
  // coach saluda después, Emmanuel 19/10), y el watch del server cuenta desde
  // que se abre.
  const start = useCallback(() => setPhase('waiting'), []);
  const goSolo = useCallback(() => {
    reqRef.current++;
    // Con la tarjeta de una pregunta que el coach YA hizo, el coach la repite
    // más corta (Emmanuel, 14/10: callado, el alumno no sabe por dónde ir).
    // Esperando al coach o desde la hoja, no: va a preguntar igual.
    if (IDLE_CARD_PHASES.has(phaseRef.current)) {
      expectedRef.current = null;
      expectedPatchRef.current = null;
      session?.sendSessionUpdate({ assistedExpect: null, assistedReask: true });
    } else if (expectedRef.current) {
      setExpected(null);
    }
    setCovered(true);
    setModel(null);
    setSoloSince(phaseRef.current === 'intro' ? null : userTurns);
    setPhase('off');
  }, [setExpected, userTurns, session]);
  const follow = useCallback(() => {
    if (!model) return;
    // «Seguir»: la frase modelo va escrita (el server la marca asistida igual
    // y la consume: acá también, para no re-mandarla en una reconexión).
    addNote(model.en, copy.bubble.followed);
    expectedRef.current = null;
    expectedPatchRef.current = null;
    session?.sendText(model.en);
    setPhase('waiting');
  }, [model, session, addNote]);
  const discard = useCallback(() => {
    if (!model) return;
    const ph = phaseRef.current;
    if (ph !== 'ready' && ph !== 'rejected' && ph !== 'silence') return;
    const sid = args.sessionId;
    if (sid) {
      void discardAssistedPhrase({
        sessionId: sid,
        phrase: model.en,
        ...(model.es.trim() ? { translation: model.es.trim() } : {}),
        ...(args.scenarioId ? { scenarioId: args.scenarioId } : {}),
      }).catch((err: unknown) => console.warn('[assisted] the discard was not recorded', err));
    }
    discardedRef.current = [...discardedRef.current, model.en];
    // Como si no hubiera llegado: ni la frase ni el número de la tarjeta.
    setExpected(null);
    setModel(null);
    setTurnNo((n) => Math.max(0, n - 1));
    void fetchModel(undefined, discardedRef.current);
  }, [model, args.sessionId, args.scenarioId, setExpected, fetchModel]);
  const repeat = useCallback(() => {
    setResult(null);
    setPhase('ready');
  }, []);
  const retry = useCallback(() => void fetchModel(), [fetchModel]);
  const keepAssisted = useCallback(() => {
    setOfferPending(false);
    practicedRef.current = new Set();
    bonusTriesRef.current = 0;
    void fetchModel();
  }, [fetchModel]);
  const restartSolo = useCallback(() => {
    if (expectedRef.current) setExpected(null);
    args.onRestartSolo();
  }, [args, setExpected]);
  const celebrationDone = useCallback(() => {
    if (phaseRef.current === 'accepted') setPhase('waiting');
  }, []);

  const marks = useMemo<KaraokeMark[]>(() => {
    if (!model) return [];
    if (phase === 'recording' || phase === 'judging') {
      return karaokeMarks(model.en, session?.transcript ?? '', saidSyllables);
    }
    return [];
  }, [model, phase, session?.transcript, saidSyllables]);

  const noteAt = useMemo(() => {
    const out = new Map<number, string>();
    for (const n of notes) {
      const i = messages.findIndex(
        (m, idx) => idx >= n.from && m.who === 'user' && m.text.trim() === n.text && !out.has(idx),
      );
      if (i >= 0) out.set(i, n.note);
    }
    return out;
  }, [notes, messages]);
  const noteFor = useCallback(
    (_text: string, index: number) => noteAt.get(index) ?? null,
    [noteAt],
  );
  const isCovered = useCallback(
    (_text: string, index: number) => covered && !ended && noteAt.has(index),
    [covered, ended, noteAt],
  );

  const cardVisible =
    phase === 'loading' ||
    phase === 'ready' ||
    phase === 'recording' ||
    phase === 'judging' ||
    phase === 'accepted' ||
    phase === 'rejected' ||
    phase === 'silence' ||
    phase === 'error' ||
    phase === 'offer' ||
    soloSince !== null;

  const deckVisible = phase !== 'off' && !ended;

  return {
    phase,
    sceneTitle: args.sceneTitle,
    cardVisible,
    deckVisible,
    offerAllDone,
    objectives: args.objectives,
    model,
    turnNo,
    marks,
    result,
    error,
    soloSince,
    noteFor,
    isCovered,
    start,
    goSolo,
    discard,
    present,
    follow,
    repeat,
    retry,
    keepAssisted,
    restartSolo,
    celebrationDone,
  };
}
